import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { HomePage } from '@/pages/HomePage';
import { EntityListPage } from '@/pages/EntityListPage';
import { EntityDetailPage } from '@/pages/EntityDetailPage';
import { EntityCreatePage } from '@/pages/EntityCreatePage';
import { GlobalSearchPage } from '@/pages/GlobalSearchPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { LoginPage } from '@/pages/LoginPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';
import { MainLayout } from '@/layouts/MainLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { FrontWindowPage } from '@/pages/CustomerServicePage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { BulkInventoryBinPage } from '@/pages/BulkInventoryBinPage';
import { BulkDisposePage } from '@/pages/BulkDisposePage';
import { BarcodeLabelsPage } from '@/pages/BarcodeLabelsPage';
import { ReservedStockCartsPage } from '@/pages/property-book/ReservedStockCartsPage';
import { PurchaseRequisitionsPage } from '@/pages/PurchaseRequisitionsPage';
import { TransactionsPage } from '@/pages/TransactionsPage';
import { EmailSchedulesPage } from '@/pages/EmailSchedulesPage';
import { AssetLossManagementPage } from '@/pages/AssetLossManagementPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { MaintenanceRequestPage } from '@/pages/MaintenanceRequestPage';
import { TransfersShippingPage } from '@/pages/TransfersShippingPage';
import { IARPage } from '@/pages/IARPage';
import { IARRunPage } from '@/pages/IARRunPage';
import { UserManagementPage } from '@/pages/UserManagementPage';
import { CorrectionPage } from '@/pages/CorrectionPage';
import { ConfigurationManagementPage } from '@/pages/ConfigurationManagementPage';
import { SystemHealthPage } from '@/pages/SystemHealthPage';
import { SystemLogsPage } from '@/pages/SystemLogsPage';
import { EQUIP_APP_ROLES } from '@equip/utils';

/** All non-viewer roles — used for routes that forbid read-only access. */
const NON_VIEWER_ROLES = [...EQUIP_APP_ROLES].filter((r) => r !== 'viewer');

// Create router configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      // Public routes
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'purchase-requisitions',
        element: (
          // viewer-safe: read-only page, writes blocked by ViewerGuardedButton + API preHandler
          <ProtectedRoute>
            <PurchaseRequisitionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'auth/callback',
        element: <AuthCallbackPage />,
      },
      {
        path: 'unauthorized',
        element: <UnauthorizedPage />,
      },

      // Protected routes
      {
        index: true,
        element: (
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'search',
        element: (
          <ProtectedRoute>
            <GlobalSearchPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'customer-service',
        element: (
          <ProtectedRoute requiredRoles={NON_VIEWER_ROLES}>
            <FrontWindowPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'customer-service/:activeTab',
        element: (
          <ProtectedRoute requiredRoles={NON_VIEWER_ROLES}>
            <FrontWindowPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'front-window',
        element: <Navigate to="/customer-service" replace />,
      },
      {
        path: 'bulk-inventory-bin',
        element: (
          // viewer-safe: read-only page, writes blocked by useWriteGuard + API preHandler
          <ProtectedRoute>
            <BulkInventoryBinPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'bulk-dispose',
        element: (
          // viewer-safe: read-only page, writes blocked by ViewerGuardedButton + API preHandler
          <ProtectedRoute>
            <BulkDisposePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'barcode-labels',
        element: (
          // viewer-safe: read-only page, writes blocked by ViewerGuardedButton + API preHandler
          <ProtectedRoute>
            <BarcodeLabelsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'property-book/reserved-stock',
        element: (
          <ProtectedRoute requiredRoles={NON_VIEWER_ROLES}>
            <ReservedStockCartsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'transactions',
        element: (
          // viewer-safe: read-only page, writes blocked by ViewerGuardedButton + API preHandler
          <ProtectedRoute>
            <TransactionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'maintenance-request',
        element: (
          // viewer-safe: read-only page, writes blocked by ViewerGuardedButton + API preHandler
          <ProtectedRoute>
            <MaintenanceRequestPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'shipping',
        element: (
          // viewer-safe: read-only page, writes blocked by ViewerGuardedButton + API preHandler
          <ProtectedRoute>
            <TransfersShippingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'transfers-shipping',
        element: (
          // viewer-safe: read-only page, writes blocked by ViewerGuardedButton + API preHandler
          <ProtectedRoute>
            <TransfersShippingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'placeholder/shipping',
        element: (
          <ProtectedRoute>
            <TransfersShippingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <NotFoundPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'email',
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <EmailSchedulesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'email-schedules',
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <EmailSchedulesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'configuration',
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <ConfigurationManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system-health',
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <SystemHealthPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system-logs',
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <SystemLogsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'asset-loss-management',
        element: (
          <ProtectedRoute>
            <AssetLossManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'iar',
        element: (
          <ProtectedRoute>
            <IARPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'iar/:runId',
        element: (
          <ProtectedRoute>
            <IARRunPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'users',
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <UserManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'placeholder/:featureKey',
        element: (
          <ProtectedRoute>
            <PlaceholderPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'correction',
        element: (
          <ProtectedRoute requiredRoles={NON_VIEWER_ROLES}>
            <CorrectionPage />
          </ProtectedRoute>
        ),
      },

      // Dynamic entity routes (clean URLs) - protected
      {
        path: ':entityType',
        element: (
          <ProtectedRoute>
            <EntityListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':entityType/create',
        element: (
          <ProtectedRoute requiredRoles={['admin', 'user']}>
            <EntityCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: ':entityType/:entityId',
        element: (
          <ProtectedRoute>
            <EntityDetailPage />
          </ProtectedRoute>
        ),
      },

      // 404 page for unmatched routes
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
